/**
 * El menú, como dato (SPEC-014 CA-12).
 *
 * Vive fuera del componente para que se pueda comprobar sin pintar nada qué ve
 * cada rol. La pregunta que responde —"¿puede un cajero llegar a la cartera
 * desde el menú?"— no se contesta mirando la pantalla con un usuario: se
 * contesta con todos los roles a la vez, y eso solo se hace en una prueba.
 */

import type { LucideIcon } from "lucide-react";
import {
  BellRing,
  Cable,
  BookOpen,
  Inbox,
  LayoutDashboard,
  Mailbox,
  PlugZap,
  PlusCircle,
  Receipt,
  Rocket,
  Store,
  SlidersHorizontal,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";

import { PERMISO, puede, type Permiso } from "../sesion/permisos.ts";
import type { Rol } from "../api/tipos.ts";

interface Entrada {
  a: string;
  texto: string;
  icono: LucideIcon;
  permiso?: Permiso;
  /** Solo estos roles la ven (SPEC-029 §12: la caja es del cajero). */
  roles?: Rol[];
  /** Estos roles no la ven aunque tengan el permiso (P-5: una pantalla por persona). */
  noPara?: Rol[];
  /** Se esconde cuando ya no hace falta (SPEC-029 §15: Empezar, hasta completarla). */
  ocultaSi?: (e: EstadoDelMenu) => boolean;
  /** Cuántos casos esperan aquí. Un número que no es cero pide la mirada. */
  cuenta?: (d: { pendientes: number; alertas: number }) => number;
}

/** Lo que el menú necesita saber de la cuenta, aparte del rol. */
export interface EstadoDelMenu {
  /** Los cinco pasos de Empezar están hechos. */
  inicioCompleto: boolean;
}

interface Grupo {
  grupo: string;
  /**
   * Plegado mientras no se esté dentro (SPEC-029 §16). Lo de soporte técnico
   * sigue a un clic para quien lo opera, sin ocupar la vista de quien no.
   */
  plegado?: boolean;
  entradas: Entrada[];
}

/**
 * El menú por persona (SPEC-029 §16, M-14). Antes eran dieciséis entradas en
 * tres grupos para todo el mundo; un cajero veía Recibidos y la API. Ahora cada
 * grupo es una pregunta: qué hay que hacer, qué me deben, cómo está montado, y
 * —plegado— por qué algo no funciona.
 *
 * «Mi cuenta» no está aquí: se llega desde el nombre de quien entró, abajo,
 * que es donde se busca.
 */
export const NAVEGACION: Grupo[] = [
  {
    grupo: "Trabajo",
    entradas: [
      { a: "/", texto: "Resumen", icono: LayoutDashboard, noPara: ["cajero"] },
      // Lo que entró hoy, en vivo, y un campo de monto (SPEC-029 §12). También
      // del dueño: en un negocio pequeño es él quien atiende el mostrador
      // cuando no hay cajero (1-oct-2026).
      { a: "/caja", texto: "Caja", icono: Store, roles: ["cajero", "dueno"] },
      {
        a: "/bandeja",
        texto: "Bandeja",
        icono: Inbox,
        cuenta: (d) => d.pendientes,
      },
      { a: "/movimientos", texto: "Pagos", icono: Receipt, noPara: ["cajero"] },
      {
        a: "/alertas",
        texto: "Alertas",
        icono: BellRing,
        noPara: ["cajero"],
        cuenta: (d) => d.alertas,
      },
    ],
  },
  {
    grupo: "Cartera",
    entradas: [
      { a: "/cartera", texto: "Facturas", icono: Wallet, permiso: PERMISO.VER_CARTERA },
      // Se mira la cartera, se detecta lo que falta, y se registra. Ese es el
      // orden del trabajo real.
      { a: "/registrar", texto: "Registrar", icono: PlusCircle, permiso: PERMISO.VER_CARTERA },
    ],
  },
  {
    grupo: "Ajustes",
    entradas: [
      // Los cinco pasos para que los pagos lleguen solos (SPEC-029 §15). Solo
      // quien configura, y solo mientras falte alguno: después, «no me llegan
      // los pagos» se responde en Recibidos.
      {
        a: "/empezar",
        texto: "Empezar",
        icono: Rocket,
        permiso: PERMISO.CONFIGURAR,
        ocultaSi: (e) => e.inicioCompleto,
      },
      {
        a: "/configuracion",
        texto: "Configuración",
        icono: SlidersHorizontal,
        permiso: PERMISO.CONFIGURAR,
      },
      { a: "/equipo", texto: "Equipo", icono: Users, permiso: PERMISO.VER_EQUIPO },
    ],
  },
  {
    grupo: "Ayuda técnica",
    plegado: true,
    entradas: [
      // El cuadre, cuánto se resolvió solo y el umbral de certeza (SPEC-029 §16).
      { a: "/metricas", texto: "Métricas", icono: TrendingUp, permiso: PERMISO.VER_CARTERA },
      { a: "/recibidos", texto: "Recibidos", icono: Mailbox, noPara: ["cajero"] },
      // Junto a Recibidos porque es el mismo problema con otra puerta: aquello
      // es lo que llegó por correo y no se pudo procesar; esto, lo que mandó el
      // sistema conectado. Sin contador a propósito: exigiría una consulta más
      // en cada pantalla para una lista que casi siempre está vacía.
      {
        a: "/cargas-rechazadas",
        texto: "Cargas rechazadas",
        icono: PlugZap,
        permiso: PERMISO.VER_CARTERA,
      },
      // Qué sistemas están conectados y si siguen recogiendo (SPEC-026).
      { a: "/conexiones", texto: "Conexiones", icono: Cable, permiso: PERMISO.VER_CARTERA },
      // La documentación pública: quien decide integrar es el dueño, y sin un
      // enlace aquí tendría que saberse la URL de memoria.
      { a: "/integracion", texto: "API para integrar", icono: BookOpen, noPara: ["cajero"] },
    ],
  },
];

/**
 * Lo que ve un rol. Una entrada sin `permiso` la ve todo el mundo.
 *
 * Los grupos que se quedan sin entradas no salen: un encabezado "Cartera" sobre
 * el vacío le dice a un cajero que hay algo ahí que no le dejan ver, y eso no
 * es discreción, es una invitación a preguntar por qué.
 */
export function navegacionPara(rol: Rol | undefined, estado: EstadoDelMenu = { inicioCompleto: false }) {
  return NAVEGACION.map((g) => ({
    ...g,
    entradas: g.entradas.filter(
      (e) =>
        (!e.permiso || puede(rol, e.permiso)) &&
        (!e.roles || (!!rol && e.roles.includes(rol))) &&
        !(rol && e.noPara?.includes(rol)) &&
        !e.ocultaSi?.(estado),
    ),
  })).filter((g) => g.entradas.length > 0);
}

/** Todas las rutas del menú, sin agrupar. */
export const RUTAS_DEL_MENU = NAVEGACION.flatMap((g) => g.entradas.map((e) => e.a));
